/**
 * Pure validation for verifikationer. Single source of truth for what may be
 * booked. No database access.
 */
import { formatSek, sum } from '../money';
import type { Account, FiscalYear, IsoDate, VerifikationRow } from '../types';
import { isWithin } from '../fiscal-years/service';
import { firstOpenDate, formatSwedishDate } from '../periods/deadlines';

export type VerifikationRuleError =
  | { code: 'too_few_rows'; message: string }
  | { code: 'unbalanced'; message: string }
  | { code: 'negative_amount'; message: string; row: number }
  | { code: 'empty_row'; message: string; row: number }
  | { code: 'both_sides'; message: string; row: number }
  | { code: 'inactive_account'; message: string; row: number }
  | { code: 'outside_fiscal_year'; message: string }
  | { code: 'fiscal_year_closed'; message: string }
  | { code: 'missing_description'; message: string }
  | { code: 'period_locked'; message: string }
  | { code: 'ib_series_only_balance_accounts'; message: string; row: number };

export type VerifikationRuleResult = { ok: true } | { ok: false; errors: VerifikationRuleError[] };

export interface VerifikationDraft {
  date: IsoDate;
  description: string;
  rows: VerifikationRow[];
}

export interface ValidateContext {
  /** Latest period lock; every date <= lockedThrough is refused. */
  lockedThrough?: IsoDate;
  /** IB series: only balance accounts (class 1-2) may be used. */
  ibSeries?: boolean;
}

/** Balance accounts are BAS class 1 (assets) and 2 (equity and liabilities). */
export function isBalanceAccount(accountNumber: number): boolean {
  return accountNumber >= 1000 && accountNumber <= 2999;
}

export function validateVerifikation(
  draft: VerifikationDraft,
  fiscalYear: FiscalYear,
  activeAccounts: ReadonlyMap<number, Account>,
  ctx: ValidateContext = {},
): VerifikationRuleResult {
  const errors: VerifikationRuleError[] = [];

  if (!draft.description.trim()) {
    errors.push({ code: 'missing_description', message: 'Verifikationen måste ha en beskrivning' });
  }
  if (fiscalYear.status === 'closed') {
    errors.push({ code: 'fiscal_year_closed', message: 'Räkenskapsåret är stängt' });
  }
  if (!isWithin(draft.date, fiscalYear)) {
    errors.push({ code: 'outside_fiscal_year', message: 'Datumet ligger utanför räkenskapsåret' });
  }
  if (ctx.lockedThrough !== undefined && draft.date <= ctx.lockedThrough) {
    errors.push({ code: 'period_locked', message: periodLockedMessage(ctx.lockedThrough) });
  }
  if (draft.rows.length < 2) {
    errors.push({ code: 'too_few_rows', message: 'En verifikation måste ha minst två rader' });
  }

  draft.rows.forEach((row, i) => {
    if (!Number.isInteger(row.debit) || !Number.isInteger(row.credit)) {
      errors.push({ code: 'negative_amount', message: 'Belopp måste vara heltal i öre', row: i });
      return;
    }
    if (row.debit < 0 || row.credit < 0) {
      errors.push({ code: 'negative_amount', message: 'Belopp får inte vara negativa', row: i });
    }
    if (row.debit === 0 && row.credit === 0) {
      errors.push({ code: 'empty_row', message: 'Raden saknar belopp', row: i });
    }
    if (row.debit > 0 && row.credit > 0) {
      errors.push({ code: 'both_sides', message: 'En rad kan inte ha både debet och kredit', row: i });
    }
    const account = activeAccounts.get(row.accountNumber);
    if (!account || !account.active) {
      errors.push({ code: 'inactive_account', message: `Konto ${row.accountNumber} är inte aktivt i kontoplanen`, row: i });
    }
    if (ctx.ibSeries && !isBalanceAccount(row.accountNumber)) {
      errors.push({
        code: 'ib_series_only_balance_accounts',
        message: `Ingående balanser kan bara bokföras på balanskonton (klass 1–2), inte ${row.accountNumber}`,
        row: i,
      });
    }
  });

  const debit = sum(draft.rows.map((r) => r.debit));
  const credit = sum(draft.rows.map((r) => r.credit));
  if (debit !== credit) {
    errors.push({ code: 'unbalanced', message: `Debet ${formatSek(debit)} och kredit ${formatSek(credit)} balanserar inte` });
  }

  return errors.length ? { ok: false, errors } : { ok: true };
}

/**
 * Spec copy for a booking into a locked period: "Perioden är låst – rättelsen
 * bokförs 1 okt" (the first open day, with the year only when it differs).
 */
export function periodLockedMessage(lockedThrough: IsoDate): string {
  const open = firstOpenDate(lockedThrough);
  return `Perioden är låst – rättelsen bokförs ${formatSwedishDate(open, { relativeTo: lockedThrough })}`;
}

/** Rows that exactly reverse the given rows (debit <-> credit). */
export function reverseRows(rows: readonly VerifikationRow[]): VerifikationRow[] {
  return rows.map((r) => ({ ...r, debit: r.credit, credit: r.debit }));
}
